import type { Maybe, ProductImage, ProductVideo } from '@pylot-data/pylotschema'
import Image from '@corratech/pylot-image'
import dynamic from 'next/dynamic'
import { useDevice } from '@corratech/pylot-utils'
import { useTranslation } from 'next-i18next/pages'

const ZoomImage = dynamic(() => import('@corratech/pylot-zoom-image'))
const PrismaZoom = dynamic(() => import('react-prismazoom'))

type ImageSlidePropTypes = {
    image: Maybe<ProductVideo | ProductImage>
    alt?: string
    width: number
    height: number
    index: number
    /** Prioriza la primera imagen real de la galería (p. ej. cuando el slide 0 es video). */
    priority?: boolean
    zoom: boolean
    showFullscreen: boolean
    s: {
        [key: string]: string
    }
    openFullscreen: (i: number) => void
}

const ImageSlide = (props: ImageSlidePropTypes) => {
    const {
        image,
        alt,
        width,
        height,
        index,
        priority,
        zoom,
        showFullscreen,
        s,
        openFullscreen
    } = props
    const { t } = useTranslation(['common'])
    const isPriority = priority ?? index === 0

    const { isTouch } = useDevice()

    const zoomSlide = (
        img: Maybe<ProductVideo | ProductImage>,
        alt: string,
        imgWidth: number,
        imgHeight: number,
        slideIndex: number
    ) => {
        return isTouch() ? (
            <PrismaZoom>
                {slide(
                    img,
                    alt || t('Product Image'),
                    imgWidth,
                    imgHeight,
                    slideIndex
                )}
            </PrismaZoom>
        ) : (
            <ZoomImage
                alt={img?.label || alt}
                src={img?.url ?? ''}
                zoomSrc={img?.url ? `${img.url}?width=2000` : ''}
            />
        )
    }

    const slide = (
        img: Maybe<ProductVideo | ProductImage>,
        alt: string,
        imgWidth: number,
        imgHeight: number,
        slideIndex: number
    ) => {
        return (
            <Image
                className={s['img']}
                src={img?.url ?? ''}
                alt={img?.label || alt}
                style={{ objectFit: 'contain' }}
                width={imgWidth}
                height={imgHeight}
                priority={isPriority}
                loading={isPriority ? 'eager' : undefined}
                quality="85"
                onClick={() => {
                    showFullscreen && openFullscreen(slideIndex)
                }}
            />
        )
    }

    return zoom
        ? zoomSlide(image, alt || t('Product Image'), width, height, index)
        : slide(image, alt || t('Product Image'), width, height, index)
}

export default ImageSlide
